import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hanko } from '@teamhanko/hanko-elements';
import { authEvents, isJwtExpired } from '../api/apiClient';
import { notification } from 'antd';
import { config } from '../config';
import {
    consumePostLoginRedirectTarget,
    isAuthRoute,
    rememberCurrentLocationForLogin
} from '../utils/authRedirect';
import { consumePendingAuthTrackingEvent } from '../analytics/authTrackingQueue';
import { buildTrackingMeasurementContext } from '../analytics/measurementContext';
import { trackAppEventOnce } from '../analytics/trackEvent';

const hankoApi = config.hankoUrl;
const BFF_URL = config.bffUrl;
const hanko = new Hanko(hankoApi);

// hanko.getCurrentUser() (Hanko v3) returns emails as an array instead of the
// flat `email` field the old getUser() response had; the rest of the app
// still reads user.email directly, so derive it here in one place.
const withPrimaryEmail = (userData) => ({
    ...userData,
    email: userData.email ?? userData.emails?.find((e) => e.is_primary)?.address ?? userData.emails?.[0]?.address,
});

export const AuthContext = createContext(null);

// LocalStorage keys that should survive logout (UI preferences, not user data)
const LOCALSTORAGE_KEYS_TO_KEEP = [
    'tendera-selected-columns',
    'tendera-mybids-selected-columns',
    'tendera-myprofiles-selected-columns',
];

const clearLocalStorageExcept = (keysToKeep) => {
    const keep = new Set(keysToKeep);
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const key = localStorage.key(i);
        if (key && !keep.has(key)) {
            localStorage.removeItem(key);
        }
    }
};

// Shared state to prevent multiple API calls
let onboardingCache = {
    userId: null,
    status: null,
    timestamp: null
};

// Track the last known user ID to detect user changes
let lastKnownUserId = null;

// Helper to extract the JWT from the hanko cookie
const getSessionTokenFromCookie = () => {
    const match = document.cookie.match(/(^| )hanko=([^;]+)/);
    return match ? match[2] : null;
};

const buildBffUrl = (path) => {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${BFF_URL}${BFF_URL?.endsWith('/') ? '' : '/'}${normalizedPath}`;
};

const normalizeBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

const normalizeMeasurementContext = (context, userData) => {
    if (!context) return null;
    const internalAccount = normalizeBoolean(context.internal_account ?? context.internalAccount);
    const internalUser = normalizeBoolean(context.is_internal_user ?? context.isInternalUser) || internalAccount;

    return {
        ...context,
        user_id: context.user_id || context.userId || userData?.id || null,
        account_id: context.account_id || context.accountId || context.organization_id || context.organizationId || null,
        company_id: context.company_id || context.companyId || context.organization_id || context.organizationId || null,
        company_name: context.company_name || context.companyName || context.officialName || null,
        company_domain: context.company_domain || context.companyDomain || null,
        business_code: context.business_code || context.businessCode || null,
        current_plan: context.current_plan || context.currentPlan || context.plan?.planType || context.plan?.name || (typeof context.plan === 'string' ? context.plan : null),
        is_internal_user: internalUser,
        internal_account: internalAccount,
        internal_reason: context.internal_reason || context.internalReason || null,
        environment: context.environment || import.meta.env.MODE
    };
};

const fetchAppJson = async (path, token, optionalStatuses = []) => {
    const response = await fetch(buildBffUrl(path), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    });

    if (optionalStatuses.includes(response.status)) {
        return null;
    }

    const text = await response.text();
    let parsed = null;
    if (text) {
        try {
            parsed = JSON.parse(text);
        } catch {
            parsed = null;
        }
    }

    if (!response.ok) {
        const error = new Error(`API request failed: ${response.status}`);
        error.status = response.status;
        error.data = parsed ?? (text || null);
        throw error;
    }

    return parsed;
};

const resolveSubscriptionPlan = (subscription) => {
    if (!subscription) return null;
    return (
        subscription.current_plan ||
        subscription.currentPlan ||
        subscription.plan?.planType ||
        subscription.plan?.name ||
        subscription.planType ||
        subscription.name ||
        null
    );
};

const buildMeasurementContextFromExistingApi = (userData, appUser, organization, subscription) => normalizeMeasurementContext({
    user_id: appUser?.id || appUser?.user_id || userData?.id || null,
    account_id: organization?.id || organization?.account_id || organization?.accountId || appUser?.organization_id || appUser?.organizationId || null,
    company_id: organization?.id || organization?.company_id || organization?.companyId || null,
    company_name: organization?.officialName || organization?.company_name || organization?.companyName || null,
    company_domain: organization?.domain || organization?.company_domain || organization?.companyDomain || null,
    business_code: organization?.businessCode || organization?.business_code || null,
    current_plan: resolveSubscriptionPlan(subscription),
    is_internal_user: normalizeBoolean(appUser?.is_internal_user ?? appUser?.isInternalUser) || normalizeBoolean(organization?.internal_account ?? organization?.internalAccount),
    internal_account: normalizeBoolean(organization?.internal_account ?? organization?.internalAccount),
    internal_reason: appUser?.internal_reason || appUser?.internalReason || organization?.internal_reason || organization?.internalReason || null
}, userData);

const getSettledValue = (result) => (result.status === 'fulfilled' ? result.value : null);

const fetchMeasurementContext = async (userData) => {
    const token = getSessionTokenFromCookie();
    if (!token) return null;

    const dedicatedContext = await fetchAppJson(
        'api/users/current/measurement-context',
        token,
        [204, 404, 501]
    );
    if (dedicatedContext) {
        return normalizeMeasurementContext(dedicatedContext, userData);
    }

    const [appUserResult, organizationResult, subscriptionResult] = await Promise.allSettled([
        fetchAppJson('api/users/current', token, [204, 404]),
        fetchAppJson('api/users/current/organization', token, [204, 404]),
        fetchAppJson('api/subscription/current', token, [204, 404])
    ]);

    return buildMeasurementContextFromExistingApi(
        userData,
        getSettledValue(appUserResult),
        getSettledValue(organizationResult),
        getSettledValue(subscriptionResult)
    );
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [isOnboarded, setIsOnboarded] = useState(null);
    const [onboardingStatusUnavailable, setOnboardingStatusUnavailable] = useState(false);
    const [user, setUser] = useState(null);
    const [measurementContext, setMeasurementContext] = useState(null);
    const [measurementContextUnavailable, setMeasurementContextUnavailable] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const refreshMeasurementContext = useCallback(async (userOverride = user) => {
        if (!userOverride?.id) {
            setMeasurementContext(null);
            setMeasurementContextUnavailable(false);
            return null;
        }

        try {
            const context = await fetchMeasurementContext(userOverride);
            setMeasurementContext(context);
            setMeasurementContextUnavailable(!context?.account_id && !context?.company_id);
            return context;
        } catch (error) {
            setMeasurementContext(null);
            setMeasurementContextUnavailable(true);
            console.warn('Measurement context unavailable:', error);
            return null;
        }
    }, [user]);

    // Listen for auth events (e.g., 401 responses from API)
    useEffect(() => {
        const unsubscribe = authEvents.on((event) => {
            if (event.type === 'TOKEN_EXPIRED') {
                // Terminate the Hanko session server-side too. Clearing only the
                // app-origin cookie copy leaves the session alive on the Hanko
                // API origin, and the login widget silently resumes it — the BFF
                // keeps rejecting the stale token and login loops forever
                // (clearing site data doesn't help since the session cookie
                // lives on the Hanko origin).
                hanko.logout().catch(() => {});
                // Clear auth state
                setIsAuthenticated(false);
                onboardingCache = {
                    userId: null,
                    status: null,
                    timestamp: null
                };
                setOnboardingStatusUnavailable(false);
                setMeasurementContext(null);
                setMeasurementContextUnavailable(false);
                setAuthChecked(true);

                // Show notification and redirect
                notification.error({
                    message: 'Session Expired',
                    description: event.message,
                    duration: 4.5,
                });

                rememberCurrentLocationForLogin();
                navigate('/login');
            }
            if (event.type === 'ACCESS_BLOCKED') {
                if (event.payload) {
                    sessionStorage.setItem('blockedAccess', JSON.stringify(event.payload));
                }
                navigate('/blocked', { replace: true, state: event.payload || null });
            }
        });

        return unsubscribe;
    }, [navigate]);

    useEffect(() => {
        const checkSession = async () => {
            try {
                const session = await hanko.validateSession();
                setIsAuthenticated(session.is_valid);
                setAuthChecked(true);

                if (session.is_valid) {
                    const userData = withPrimaryEmail(await hanko.getCurrentUser());

                    // Check if user has changed (new user logged in from same browser)
                    // If so, clear user-specific cached data to avoid showing old user's data
                    if (lastKnownUserId && lastKnownUserId !== userData.id) {
                        // Only clear user-specific search data, not UI preferences
                        localStorage.removeItem('noticeSearchForm');
                        localStorage.removeItem('noticeSearchResults');
                        localStorage.removeItem('noticeSearchPage');
                        localStorage.removeItem('noticeSearchExpandedRows');
                        localStorage.removeItem('noticeSearchTotalCount');
                        localStorage.removeItem('noticeSearchSortBy');
                        localStorage.removeItem('noticeSearchSortOrder');
                        localStorage.removeItem('selectedSearchProfileId');
                        localStorage.removeItem('userClearedSearchProfile');
                        // Note: Keep 'hasSearchBeenRun' since it will be set by the search that runs on login
                    }

                    // Update the last known user ID
                    lastKnownUserId = userData.id;
                    setUser(userData);
                    void refreshMeasurementContext(userData);

                    const isCacheValid =
                        onboardingCache.userId === userData.id &&
                        onboardingCache.status !== null &&
                        onboardingCache.timestamp &&
                        (Date.now() - onboardingCache.timestamp < 60000);

                    if (isCacheValid) {
                        setIsOnboarded(onboardingCache.status);
                        setOnboardingStatusUnavailable(false);
                    } else {
                        try {
                            const res = await callApi('GET', `/api/users/${userData.id}/onboarding-status`);

                            onboardingCache = {
                                userId: userData.id,
                                status: res.onboardingComplete,
                                timestamp: Date.now()
                            };

                            setIsOnboarded(res.onboardingComplete);
                            setOnboardingStatusUnavailable(false);
                        } catch (err) {
                            // 503: BFF down. 401 with status set: callApi's
                            // "backend rejected a still-valid token" case (a
                            // genuinely expired session never reaches here with
                            // a status — callApi already redirected). Both mean
                            // the session is fine but the backend isn't →
                            // degrade to the status-holding page instead of
                            // bouncing a freshly logged-in user back to /login.
                            if (err && (err.status === 503 || err.status === 401)) {
                                setIsAuthenticated(true);
                                setOnboardingStatusUnavailable(true);
                                return;
                            }
                            throw err;
                        }
                    }

                    // Safety net: if a pending invite token survived to this point it means
                    // HankoAuth.onSessionCreated didn't handle the redirect (e.g. Hanko kept
                    // the user on /register after email-verified registration, or the component
                    // was unmounted before the session fired). Redirect now that we have full
                    // auth + onboarding state.
                    const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
                    if (pendingInviteToken) {
                        sessionStorage.removeItem('pendingInviteToken');
                        navigate(`/accept-invite?token=${encodeURIComponent(pendingInviteToken)}`, { replace: true });
                        return;
                    }

                    const currentTarget = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                    if (isAuthRoute(currentTarget)) {
                        const redirectTarget = consumePostLoginRedirectTarget();
                        if (redirectTarget && redirectTarget !== currentTarget) {
                            navigate(redirectTarget, { replace: true });
                            return;
                        }
                    }
                } else {
                    setIsAuthenticated(false);
                    setOnboardingStatusUnavailable(false);
                    setMeasurementContext(null);
                    setMeasurementContextUnavailable(false);
                    setAuthChecked(true);
                }
            } catch (error) {
                console.error('Session check failed:', error);
                if (error && error.code === 'EMAIL_DOMAIN_BLOCKED') {
                    return;
                }
                setIsAuthenticated(false);
                setOnboardingStatusUnavailable(false);
                setMeasurementContext(null);
                setMeasurementContextUnavailable(false);
                setAuthChecked(true);
            } finally {
                setLoading(false);
            }
        };

        checkSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isAuthenticated || !user || (!measurementContext && !measurementContextUnavailable)) {
            return;
        }

        const pendingEvent = consumePendingAuthTrackingEvent();
        if (!pendingEvent) return;

        const isInternalMeasurementUser = Boolean(
            measurementContext?.is_internal_user ||
            measurementContext?.internal_account
        );

        trackAppEventOnce(
            `auth-completed:${pendingEvent.eventName}:${pendingEvent.queuedAt || user.id}`,
            pendingEvent.eventName,
            pendingEvent.properties || {},
            buildTrackingMeasurementContext(user, measurementContext, isInternalMeasurementUser)
        );
    }, [isAuthenticated, measurementContext, measurementContextUnavailable, user]);

    const logout = async (redirectTo = '/login') => {
        try {
            await hanko.logout();
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            // Clear onboarding cache on logout
            onboardingCache = {
                userId: null,
                status: null,
                timestamp: null
            };
            setIsAuthenticated(false);
            setOnboardingStatusUnavailable(false);
            setAuthChecked(true);
            setUser(null);
            setMeasurementContext(null);
            setMeasurementContextUnavailable(false);
            sessionStorage.removeItem('blockedAccess');
            clearLocalStorageExcept(LOCALSTORAGE_KEYS_TO_KEEP);
            navigate(redirectTo, { replace: true });
        }
    };

    const callApi = useCallback(async (method, url, body = null) => {
        const token = getSessionTokenFromCookie();
        if (!token) {
            rememberCurrentLocationForLogin();
            navigate('/login');
            throw new Error('No session token found in cookie');
        }
        const fullUrl = `${BFF_URL}${url.startsWith('/') ? '' : '/'}${url}`;
        const serializedBody = body !== null && body !== undefined ? JSON.stringify(body) : null;

        const response = await fetch(fullUrl, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: serializedBody
        });

        // Handle 401 Unauthorized
        if (response.status === 401) {
            // A 401 with a still-valid token is a server-side auth fault, not
            // an expired session — logging out would destroy a good session
            // and strand the user in a login loop for as long as the backend
            // misbehaves. Keep the session and surface the fault instead.
            if (!isJwtExpired(token)) {
                console.warn('Fetch API: backend rejected a still-valid session token (401)');
                notification.error({
                    key: 'auth-backend-401',
                    message: 'Palveluvirhe',
                    description: 'Palvelin hylkäsi voimassa olevan istunnon (401). Kirjautumisesi on kunnossa — taustapalvelussa on todennäköisesti tilapäinen häiriö.',
                    duration: 6,
                });
                const err = new Error('Backend rejected a valid session token (401)');
                err.status = 401;
                throw err;
            }
            console.warn('Fetch API: Token expired or unauthorized');
            // Kill the Hanko session server-side as well — otherwise the login
            // widget resumes the stale session and login loops (see the
            // TOKEN_EXPIRED handler above).
            hanko.logout().catch(() => {});
            // Clear the token from cookie
            document.cookie = 'hanko=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';

            // Clear auth state
            setIsAuthenticated(false);
            onboardingCache = {
                userId: null,
                status: null,
                timestamp: null
            };
            setMeasurementContext(null);
            setMeasurementContextUnavailable(false);

            // Show notification and redirect
            notification.error({
                message: 'Session Expired',
                description: 'Your session has expired. Please log in again.',
                duration: 4.5,
            });

            rememberCurrentLocationForLogin();
            navigate('/login');
            throw new Error('Unauthorized - session expired');
        }

        if (response.status === 403) {
            const text = await response.text();
            let data = null;
            if (text) {
                try {
                    data = JSON.parse(text);
                } catch (e) {
                    data = null;
                }
            }

            if (data && data.code === 'EMAIL_DOMAIN_BLOCKED') {
                authEvents.emit({
                    type: 'ACCESS_BLOCKED',
                    message: data.message,
                    title: data.title,
                    supportEmail: data.supportEmail,
                    status: 403,
                    payload: data
                });
                const err = new Error('Access blocked');
                err.code = 'EMAIL_DOMAIN_BLOCKED';
                err.payload = data;
                throw err;
            }
        }

        const text = await response.text();
        let parsed = null;
        if (text) {
            try {
                parsed = JSON.parse(text);
            } catch (e) {
                parsed = null;
            }
        }

        if (!response.ok) {
            const error = new Error(`API call failed: ${response.status}`);
            error.status = response.status;
            error.data = parsed ?? (text || null);
            error.response = { status: response.status, data: error.data };
            throw error;
        }
        // Handle empty responses gracefully
        if (!text) return {};
        if (parsed !== null) return parsed;
        throw new Error(`API response parse failed: ${text}`);
    }, [navigate]);

    // Like callApi but returns the raw Response for streaming (SSE).
    // Caller is responsible for reading response.body.
    const callApiStream = useCallback(async (method, url, body = null, options = {}) => {
        const token = getSessionTokenFromCookie();
        if (!token) {
            rememberCurrentLocationForLogin();
            navigate('/login');
            throw new Error('No session token found in cookie');
        }
        const fullUrl = `${BFF_URL}${url.startsWith('/') ? '' : '/'}${url}`;
        const response = await fetch(fullUrl, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: body ? JSON.stringify(body) : null,
            signal: options.signal
        });
        if (response.status === 401) {
            // Same stale-vs-server-fault split as callApi's 401 path.
            if (!isJwtExpired(token)) {
                console.warn('Stream API: backend rejected a still-valid session token (401)');
                const err = new Error('Backend rejected a valid session token (401)');
                err.status = 401;
                throw err;
            }
            hanko.logout().catch(() => {});
            document.cookie = 'hanko=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
            rememberCurrentLocationForLogin();
            navigate('/login');
            throw new Error('Unauthorized - session expired');
        }
        if (!response.ok) {
            throw new Error(`API call failed: ${response.status}`);
        }
        return response;
    }, [navigate]);

    // Function to update onboarding status in cache and state
    const updateOnboardingStatus = (status) => {
        if (user) {
            // Update cache
            onboardingCache = {
                userId: user.id,
                status: status,
                timestamp: Date.now()
            };
            // Update state
            setIsOnboarded(status);
            setOnboardingStatusUnavailable(false);
            if (status) {
                void refreshMeasurementContext(user);
            }
        }
    };

    const refreshOnboardingStatus = useCallback(async () => {
        if (!user) return null;
        try {
            const res = await callApi('GET', `/api/users/${user.id}/onboarding-status`);
            onboardingCache = {
                userId: user.id,
                status: res.onboardingComplete,
                timestamp: Date.now()
            };
            setIsOnboarded(res.onboardingComplete);
            setOnboardingStatusUnavailable(false);
            return res.onboardingComplete;
        } catch (err) {
            if (err && err.status === 503) {
                setOnboardingStatusUnavailable(true);
                return null;
            }
            throw err;
        }
    }, [callApi, user]);

    const value = {
        isAuthenticated,
        authChecked,
        isOnboarded,
        onboardingStatusUnavailable,
        user,
        measurementContext,
        measurementContextUnavailable,
        isInternalMeasurementUser: Boolean(measurementContext?.is_internal_user || measurementContext?.internal_account),
        loading,
        logout,
        callApi,
        callApiStream,
        updateOnboardingStatus,
        refreshOnboardingStatus,
        refreshMeasurementContext
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
